import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { Input } from '@components/Input';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { companyService } from '@services/companyService';
import { Company, CompanyFormData, formatCnpj, formatCep, formatPhone, onlyDigits } from '@/types/company';
import { InvoiceNumberControlTab } from './InvoiceNumberControlTab';
import { CompanyCertificateCard } from './CompanyCertificateCard';

const emptyForm: CompanyFormData = {
  document: '',
  name: '',
  addr_street: '',
  addr_num: '',
  addr_compl: '',
  addr_district: '',
  addr_city: '',
  addr_state: '',
  addr_zip: '',
  addr_country: 'Brasil',
  state_registration_tax_id: '',
  cnae_code1: '',
  cnae_code2: '',
  phone: '',
  email: '',
  tax_regime_crt: 1,
  city_ibge_code: '',
};

export const CompanyFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { hasRole } = usePermissions();
  const isEditing = Boolean(id);
  const requiredPermission = isEditing ? 'companies.write' : 'companies.create';
  const canEditRestrictedFields = hasRole('admin');

  const [tab, setTab] = useState<'form' | 'numbering'>('form');
  const [formData, setFormData] = useState<CompanyFormData>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Certificado digital A1: a empresa carregada alimenta o card (validade, CN,
  // último envio). O card cuida do envio e do teste; aqui só recarregamos
  // depois, para o selo de validade refletir o arquivo novo.
  const [company, setCompany] = useState<Company | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isEditing || !id) return;

    const loadItem = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await companyService.getCompanyById(Number(id));
        setFormData({
          document: data.document || '',
          name: data.name || '',
          addr_street: data.addr_street || '',
          addr_num: data.addr_num || '',
          addr_compl: data.addr_compl || '',
          addr_district: data.addr_district || '',
          addr_city: data.addr_city || '',
          addr_state: data.addr_state || '',
          addr_zip: data.addr_zip || '',
          addr_country: data.addr_country || 'Brasil',
          state_registration_tax_id: data.state_registration_tax_id || '',
          cnae_code1: data.cnae_code1 || '',
          cnae_code2: data.cnae_code2 || '',
          phone: data.phone || '',
          email: data.email || '',
          tax_regime_crt: data.tax_regime_crt ?? 1,
          city_ibge_code: data.city_ibge_code || '',
        });
        setCompany(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar empresa');
      } finally {
        setLoading(false);
      }
    };

    loadItem();
  }, [id, isEditing, reloadKey]);

  const title = useMemo(
    () => (isEditing ? 'Editar Empresa' : 'Nova Empresa'),
    [isEditing],
  );

  const updateField = (field: keyof CompanyFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const documentDigits = onlyDigits(formData.document);
    const zipDigits = onlyDigits(formData.addr_zip);
    const phoneDigits = onlyDigits(formData.phone);

    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    if (!documentDigits) errors.document = 'CNPJ é obrigatório';
    if (documentDigits && documentDigits.length !== 14) errors.document = 'CNPJ deve conter 14 dígitos';
    if (zipDigits && zipDigits.length !== 8) errors.addr_zip = 'CEP deve conter 8 dígitos';
    if (formData.addr_state && formData.addr_state.trim().length !== 2) errors.addr_state = 'UF deve conter 2 caracteres';
    if (phoneDigits && phoneDigits.length < 10) errors.phone = 'Telefone inválido';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'E-mail inválido';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = (): CompanyFormData => ({
    document: onlyDigits(formData.document).slice(0, 14),
    name: formData.name.trim(),
    addr_street: formData.addr_street?.trim() || null,
    addr_num: formData.addr_num?.trim() || null,
    addr_compl: formData.addr_compl?.trim() || null,
    addr_district: formData.addr_district?.trim() || null,
    addr_city: formData.addr_city?.trim() || null,
    addr_state: formData.addr_state?.trim().toUpperCase().slice(0, 2) || null,
    addr_zip: onlyDigits(formData.addr_zip).slice(0, 8) || null,
    addr_country: formData.addr_country?.trim() || null,
    state_registration_tax_id: formData.state_registration_tax_id?.trim() || null,
    cnae_code1: formData.cnae_code1?.trim() || null,
    cnae_code2: formData.cnae_code2?.trim() || null,
    phone: onlyDigits(formData.phone).slice(0, 11) || null,
    email: formData.email?.trim() || null,
    tax_regime_crt: formData.tax_regime_crt ?? 1,
    city_ibge_code: onlyDigits(formData.city_ibge_code).slice(0, 7) || null,
  });

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const payload = buildPayload();
      // O certificado A1 não entra aqui: ele é enviado no card próprio, com o
      // arquivo e a senha juntos. Salvar o cadastro nunca mexe no certificado.
      if (isEditing && id) {
        await companyService.updateCompany(Number(id), payload);
      } else {
        await companyService.createCompany(payload);
      }

      navigate('/settings/fiscal/companies');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar empresa');
    } finally {
      setSaving(false);
    }
  };

  const restrictedFieldClass = !canEditRestrictedFields && isEditing ? 'bg-slate-100 text-slate-500' : '';

  return (
    <PermissionGuard
      permission={requiredPermission}
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para acessar esta tela.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/fiscal/companies')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">{title}</h1>
            <p className="text-slate-600">
              {isEditing
                ? 'Atualize os dados fiscais e cadastrais da empresa.'
                : 'Preencha os dados da nova empresa do grupo.'}
            </p>
          </div>
        </div>

        <div className="flex gap-1 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setTab('form')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'form' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            Cadastro
          </button>
          <button
            type="button"
            onClick={() => setTab('numbering')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'numbering' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            Numeração de Notas
          </button>
        </div>

        {tab === 'numbering' ? (
          isEditing && id ? (
            <InvoiceNumberControlTab companyId={Number(id)} />
          ) : (
            <Card>
              <CardContent>
                <p className="text-slate-600">Salve a empresa primeiro para configurar a numeração das notas fiscais.</p>
              </CardContent>
            </Card>
          )
        ) : (
          <>
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card>
            <CardContent>
              <div className="space-y-4">
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="h-14 animate-pulse rounded bg-slate-200" />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader title="Dados Principais" subtitle="Campos obrigatórios e identificação fiscal" />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label="Razão Social"
                    required
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    error={fieldErrors.name}
                    maxLength={150}
                  />
                  <Input
                    label="CNPJ"
                    required
                    value={formatCnpj(formData.document)}
                    onChange={(e) => updateField('document', onlyDigits(e.target.value).slice(0, 14))}
                    error={fieldErrors.document}
                    maxLength={18}
                    readOnly={isEditing && !canEditRestrictedFields}
                    className={restrictedFieldClass}
                  />
                  <Input
                    label="Inscrição Estadual"
                    value={formData.state_registration_tax_id || ''}
                    onChange={(e) => updateField('state_registration_tax_id', e.target.value)}
                    error={fieldErrors.state_registration_tax_id}
                    maxLength={20}
                    readOnly={isEditing && !canEditRestrictedFields}
                    className={restrictedFieldClass}
                  />
                  <Input
                    label="País"
                    value={formData.addr_country || ''}
                    onChange={(e) => updateField('addr_country', e.target.value)}
                    maxLength={100}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Endereço" subtitle="Dados fiscais da empresa para emissão e integração" />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                  <div className="md:col-span-3">
                    <Input
                      label="CEP"
                      value={formatCep(formData.addr_zip)}
                      onChange={(e) => updateField('addr_zip', onlyDigits(e.target.value).slice(0, 8))}
                      error={fieldErrors.addr_zip}
                      maxLength={9}
                    />
                  </div>
                  <div className="md:col-span-7">
                    <Input
                      label="Logradouro"
                      value={formData.addr_street || ''}
                      onChange={(e) => updateField('addr_street', e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Input
                      label="Número"
                      value={formData.addr_num || ''}
                      onChange={(e) => updateField('addr_num', e.target.value)}
                      maxLength={10}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <Input
                      label="Complemento"
                      value={formData.addr_compl || ''}
                      onChange={(e) => updateField('addr_compl', e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <Input
                      label="Bairro"
                      value={formData.addr_district || ''}
                      onChange={(e) => updateField('addr_district', e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="md:col-span-7">
                    <Input
                      label="Cidade"
                      value={formData.addr_city || ''}
                      onChange={(e) => updateField('addr_city', e.target.value)}
                      maxLength={100}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Input
                      label="UF"
                      value={formData.addr_state || ''}
                      onChange={(e) => updateField('addr_state', e.target.value.toUpperCase().slice(0, 2))}
                      error={fieldErrors.addr_state}
                      maxLength={2}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Dados Complementares" subtitle="CNAEs, contato e observações cadastrais" />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label="CNAE 1"
                    value={formData.cnae_code1 || ''}
                    onChange={(e) => updateField('cnae_code1', e.target.value)}
                    maxLength={10}
                  />
                  <Input
                    label="CNAE 2"
                    value={formData.cnae_code2 || ''}
                    onChange={(e) => updateField('cnae_code2', e.target.value)}
                    maxLength={10}
                  />
                  <Input
                    label="Telefone"
                    value={formatPhone(formData.phone)}
                    onChange={(e) => updateField('phone', onlyDigits(e.target.value).slice(0, 11))}
                    error={fieldErrors.phone}
                    maxLength={15}
                  />
                  <Input
                    label="E-mail"
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => updateField('email', e.target.value)}
                    error={fieldErrors.email}
                    maxLength={150}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                title="Emissão Fiscal (NF-e direta com a SEFAZ)"
                subtitle="Regime tributário, município IBGE do emitente e ativação da emissão direta."
              />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Regime Tributário (CRT)</label>
                    <select
                      value={formData.tax_regime_crt ?? 1}
                      onChange={(e) => setFormData((prev) => ({ ...prev, tax_regime_crt: Number(e.target.value) }))}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value={1}>1 - Simples Nacional</option>
                      <option value={2}>2 - Simples Nacional (excesso de sublimite)</option>
                      <option value={3}>3 - Regime Normal</option>
                    </select>
                  </div>
                  <Input
                    label="Código IBGE do Município (cMun)"
                    value={formData.city_ibge_code || ''}
                    onChange={(e) => updateField('city_ibge_code', onlyDigits(e.target.value).slice(0, 7))}
                    maxLength={7}
                    placeholder="7 dígitos (ex.: 3550308)"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  A emissão de NF-e é feita direto na SEFAZ. Requer certificado A1, o código IBGE do município
                  preenchido e a numeração (série/número) semeada.
                </p>
              </CardContent>
            </Card>

            {/* Certificado A1: só depois que a empresa existe — o arquivo é
                gravado num blob nomeado pelo CompanyId. */}
            {isEditing && id && (
              <CompanyCertificateCard
                companyId={Number(id)}
                company={company}
                onUploaded={() => setReloadKey((v) => v + 1)}
              />
            )}
          </>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/settings/fiscal/companies')}>
            Cancelar
          </Button>
          <Button onClick={handleSave} loading={saving} icon={<Save size={18} />}>
            {isEditing ? 'Salvar Alterações' : 'Criar Empresa'}
          </Button>
        </div>
          </>
        )}
      </div>
    </PermissionGuard>
  );
};